import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { LoadingScreen } from '@syntra/ui/components/loading-screen';

import DashboardLayout from 'src/layouts/dashboard';
import { FeatureFlag } from 'src/auth/feature-flags';
import { AuthGuard, OnboardingGuard } from 'src/auth/guard';
import FeatureFlagGuard from 'src/auth/guard/feature-flag-guard';
import { helpCenterRoutes } from 'src/domains/help-center/routes';
import { reportRoutes } from 'src/domains/business/reports/routes';
import { companyPartnerRoutes } from 'src/domains/business/company-partner/routes';
import { companyProjectRoutes } from 'src/domains/business/company-project/routes';
import { companyManagmentDashboardRoutes } from 'src/domains/company-dashboard/routes';
import { companyProjectActionRoutes } from 'src/domains/business/company-project-action/routes';
import { CompanyProjectCategoryRoutes } from 'src/domains/business/company-project-category/routes';

// ----------------------------------------------------------------------

const IndexPage = lazy(() => import('src/domains/dashboard/index'));
const SettingsPage = lazy(() => import('src/domains/dashboard/settings'));
const CompanyUsersPage = lazy(
  () => import('@src/domains/business/user-management/pages/UserManagementPage')
);
const CompanyRolesPage = lazy(
  () => import('@src/domains/business/role-management/pages/RoleManagementPage')
);
const CustomFieldsManagementPage = lazy(
  () => import('@src/domains/business/custom-fields/pages/CustomFieldsManagementPage')
);
// ----------------------------------------------------------------------
export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'settings', element: <SettingsPage /> },
      {
        path: 'settings/custom-fields',
        element: (
          <FeatureFlagGuard flag={FeatureFlag.CustomFields}>
            <CustomFieldsManagementPage />
          </FeatureFlagGuard>
        ),
      },
      { path: 'users', element: <CompanyUsersPage /> },
      { path: 'roles', element: <CompanyRolesPage /> },
      // Onboarding-gated domains (projects, partners, actions, categories, company
      // dashboard, reports) nested here as a pathless layout route so they share
      // this ONE AuthGuard/DashboardLayout/Suspense instance instead of each
      // mounting its own — that duplication was the main source of the guard/
      // layout remount flash on cross-domain navigation. OnboardingGuard is
      // scoped to just this subtree since the routes above (settings, users,
      // roles, custom fields) are intentionally reachable before onboarding
      // completes.
      {
        element: (
          <OnboardingGuard>
            <Outlet />
          </OnboardingGuard>
        ),
        children: [
          ...companyProjectRoutes,
          ...companyPartnerRoutes,
          ...companyProjectActionRoutes,
          ...CompanyProjectCategoryRoutes,
          ...companyManagmentDashboardRoutes,
          ...reportRoutes,
          ...helpCenterRoutes,
        ],
      },
    ],
  },
];
